import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector, Select } from '@ngxs/store';
import { switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import {
  Bot,
  BotIntent,
  BotTeamMember,
  BotVariable,
} from '../../models/bot.model';
import {
  CreateBot,
  GetBot,
  SwitchBotActivity,
  UpdateBot,
  UpdateBotImage,
  GetBotConnections,
  CreateBotConnection,
  UpdateBotConnection,
  UpdateBotTemplateData,
  UpdateBotVariable,
  GetBotVariables,
  GetBotIntents,
  UpdateBotIntent,
  SwitchBotNotifications,
  ClearBot,
  CreateBotShopifyTool,
  GetBotMessageBuffer,
  CreateBotMessageBuffer,
  UpdateBotMessageBuffer,
  GetBotTeam,
  GetBotFilters,
  GetBotScenarios,
  GetBotCollections,
  GetBotCategories,
  GetBotProducts,
  GetBotTags,
  UpdateBotTag,
  GetBotTeamExtended,
  GetBotMails,
  GetBotEntities,
} from './bot.actions';
import { BotService } from '../../services/bot.service';
import { UserState } from '../user/user.state';
import { User } from '../../models/user.model';
import { patch, updateItem } from '@ngxs/store/operators';
import { Title } from '@angular/platform-browser';

export interface BotStateModel {
  botId?: string;
  bot?: Bot;
  connections?: Array<any>;
  variables?: Array<BotVariable>;
  intents?: Array<BotIntent>;
  messageBuffer?: string;
  teamExtended?: BotTeamMember[];
  // dashboard
  team?: BotTeamMember[];
  statistics?: any;
  filters?: any;
  scenarios: Array<any>;
  collections: Array<any>;
  categories: Array<any>;
  products: Array<any>;
  tags: Array<any>;
  mails: Array<any>;
  entities: Array<any>;
}

@State<BotStateModel>({
  name: 'bot',
  defaults: {
    scenarios: [],
    collections: [],
    categories: [],
    products: [],
    tags: [],
    mails: [],
    entities: [],
  },
})
@Injectable()
export class BotState {
  @Select(UserState.me) user$!: Observable<User>;
  user?: User;

  constructor(private botService: BotService, private titleService: Title) {
    this.user$.subscribe((user) => {
      this.user = user;
    });
  }

  @Selector()
  static getBotId(state: BotStateModel) {
    return state.botId;
  }

  @Selector()
  static getBot(state: BotStateModel) {
    return state.bot;
  }

  @Selector()
  static getBotConnections(state: BotStateModel) {
    return state.connections;
  }

  @Selector()
  static getBotVariables(state: BotStateModel) {
    return state.variables;
  }

  @Selector()
  static getBotIntents(state: BotStateModel) {
    return state.intents;
  }

  @Selector()
  static getBotReplies(state: BotStateModel) {
    return state.intents
      ? state.intents.filter((intent) => intent.response.type == 'reply')
      : undefined;
  }

  @Selector()
  static getBotMessageBuffer(state: BotStateModel) {
    return state.messageBuffer;
  }

  @Selector()
  static getBotFilters(state: BotStateModel) {
    return state.filters;
  }

  @Selector()
  static getBotScenarios(state: BotStateModel) {
    return state.scenarios;
  }

  @Selector()
  static getBotCollections(state: BotStateModel) {
    return state.collections;
  }

  @Selector()
  static getBotCategories(state: BotStateModel) {
    return state.categories;
  }

  @Selector()
  static getBotProducts(state: BotStateModel) {
    return state.products;
  }

  @Selector()
  static getBotTags(state: BotStateModel) {
    return state.tags;
  }

  @Selector([BotState])
  static botTeamExtended(state: BotStateModel): any {
    return state.teamExtended;
  }

  @Selector([BotState])
  static getBotMails(state: BotStateModel): any {
    return state.mails;
  }

  @Selector([BotState])
  static getBotEntities(state: BotStateModel): any {
    return state.entities;
  }

  @Selector()
  static dashboardStatistics(state: BotStateModel): any {
    return state.statistics;
  }

  @Selector()
  static botImage(state: BotStateModel): string | undefined {
    return state.bot?.image;
  }

  @Action(CreateBot)
  createBot(
    { dispatch }: StateContext<BotStateModel>,
    { name, template }: CreateBot
  ) {
    return this.botService.createBot(name, template).pipe(
      switchMap((result) => {
        return result.status ? dispatch(new GetBot(result.bot_id)) : of(false);
      })
    );
  }

  @Action(GetBot)
  getBot({ getState, setState }: StateContext<BotStateModel>, { id }: GetBot) {
    const state = getState();
    setState({
      ...state,
      botId: id,
    });
    return this.botService.getBot(id).pipe(
      tap((result) => {
        if (result.status) {
          const state = getState();
          delete result.status;
          setState({
            ...state,
            bot: result,
          });

          const title = this.user?.portal.title || '';
          const botName = result.name || '';
          this.titleService.setTitle(`${botName} - ${title}`);
        }
      })
    );
  }

  @Action(UpdateBot)
  updateBot(
    { getState, setState }: StateContext<BotStateModel>,
    { bot }: UpdateBot
  ) {
    return this.botService.updateBot(bot).pipe(
      tap((result) => {
        const state = getState();
        if (result.status) {
          setState({
            ...state,
            bot,
          });
        }
      })
    );
  }

  @Action(ClearBot)
  clearBot({ getState, setState }: StateContext<BotStateModel>, {}: ClearBot) {
    const state = getState();
    setState({
      ...state,
      bot: undefined,
    });
    const title = this.user?.portal.title || '';
    this.titleService.setTitle(title);
  }

  @Action(SwitchBotActivity)
  switchBotActivity(
    { getState, setState }: StateContext<BotStateModel>,
    {}: SwitchBotActivity
  ): any {
    const state = getState();
    if (state.bot) {
      const bot = { ...state.bot };
      bot.active = !bot.active;
      return this.botService.updateBotActivity(bot.bot_id, bot.active).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              bot,
            });
          }
        })
      );
    }
  }

  @Action(SwitchBotNotifications)
  switchBotNotifications(
    { getState, setState }: StateContext<BotStateModel>,
    {}: SwitchBotNotifications
  ): any {
    const state = getState();
    if (state.bot) {
      const bot = { ...state.bot };
      bot.settings.notifications = !bot.settings.notifications;
      return this.botService
        .updateBotSettings(bot.bot_id, {
          notifications: bot.settings.notifications,
        })
        .pipe(
          tap((result) => {
            if (result.status) {
              const state = getState();
              setState({
                ...state,
                bot,
              });
            }
          })
        );
    }
  }

  @Action(UpdateBotImage)
  updateBotImage(
    { getState, setState }: StateContext<BotStateModel>,
    { fileData }: UpdateBotImage
  ): any {
    const state = getState();
    if (state.bot) {
      const bot = { ...state.bot };
      return this.botService.updateBotImage(bot.bot_id, fileData).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            bot.image = result.image;
            setState({
              ...state,
              bot,
            });
          }
        })
      );
    }
  }

  @Action(CreateBotConnection)
  createBotConnection(
    { getState, dispatch }: StateContext<any>,
    { provider, providerData }: CreateBotConnection
  ): any {
    const state = getState();
    if (this.user && state.bot) {
      return this.botService
        .createBotConnection(this.user.id, state.botId, provider, providerData)
        .pipe(
          switchMap((result) => {
            return result.status
              ? dispatch(new GetBotConnections())
              : of(false);
          })
        );
    }
  }

  @Action(GetBotConnections)
  getBotConnections(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotConnections
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotConnections(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              connections: result.connections,
            });
          }
        })
      );
    }
  }

  @Action(UpdateBotConnection)
  updateBotConnection(
    { getState, dispatch }: StateContext<BotStateModel>,
    { provider, providerData }: UpdateBotConnection
  ): any {
    const state = getState();
    if (this.user && state.botId) {
      return this.botService
        .updateBotConnection(this.user.id, state.botId, provider, providerData)
        .pipe(
          switchMap((result) => {
            return result.status
              ? dispatch(new GetBotConnections())
              : of(false);
          })
        );
    }
  }

  @Action(UpdateBotTemplateData)
  updateBotTemplateData(
    { getState, setState }: StateContext<BotStateModel>,
    { templateData }: UpdateBotTemplateData
  ): any {
    const state = getState();
    if (state.bot) {
      const bot = { ...state.bot },
        template = {
          name: bot.name,
          template: bot.template.name,
          template_data: templateData,
        };
      return this.botService.updateBotTemplate(bot.bot_id, template).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            bot.template.data = Object.assign(bot.template.data, templateData);
            setState({
              ...state,
              bot,
            });
          }
        })
      );
    }
  }

  @Action(GetBotVariables)
  getBotVariables(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotVariables
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotVariables(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              variables: result.variables,
            });
          }
        })
      );
    }
  }

  @Action(UpdateBotVariable)
  updateBotVariable(
    { getState, setState }: StateContext<BotStateModel>,
    { variable }: UpdateBotVariable
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.updateBotVariable(state.botId, variable).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState(),
              variables = state.variables ? [...state.variables] : [],
              i = variables.findIndex((v) => v.name == variable.name);
            if (i !== -1) {
              variables[i] = variable;
            } else {
              variables.push(variable);
            }
            setState({
              ...state,
              variables,
            });
          }
        })
      );
    }
  }

  @Action(GetBotIntents)
  getBotIntents(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotIntents
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotIntents(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              intents: result.intents,
            });
          }
        })
      );
    }
  }

  @Action(UpdateBotIntent)
  updateBotIntent(
    { getState, setState }: StateContext<BotStateModel>,
    { intent }: UpdateBotIntent
  ): any {
    return this.botService.updateBotIntent(intent).pipe(
      tap((result) => {
        if (result.status) {
          const state = getState(),
            intents = state.intents ? [...state.intents] : [],
            i = intents.findIndex((int) => int.intent_id == intent.intent_id);
          intents[i] = result.intent;
          setState({
            ...state,
            intents,
          });
        }
      })
    );
  }

  @Action(CreateBotShopifyTool)
  createBotShopifyTool(
    { getState }: StateContext<BotStateModel>,
    { shopId }: CreateBotShopifyTool
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.createBotShopifyTool(state.botId, shopId);
    }
  }

  @Action(GetBotMessageBuffer)
  getBotMessageBuffer(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotMessageBuffer
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotMessageBuffer(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              messageBuffer: result.tool.properties.period,
            });
          }
        })
      );
    }
  }

  @Action(CreateBotMessageBuffer)
  createBotMessageBuffer(
    { getState }: StateContext<BotStateModel>,
    { period }: CreateBotMessageBuffer
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.createBotMessageBuffer(state.botId, period);
    }
  }

  @Action(UpdateBotMessageBuffer)
  updateBotMessageBuffer(
    { getState }: StateContext<BotStateModel>,
    { period }: UpdateBotMessageBuffer
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.updateBotMessageBuffer(state.botId, period);
    }
  }

  @Action(GetBotFilters)
  getBotFilters(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotFilters
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotFilters(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              filters: result.filters,
            });
          }
        })
      );
    }
  }

  @Action(GetBotScenarios)
  getBotScenarios(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotScenarios
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotScenarios(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              scenarios: result.scenarios,
            });
          }
        })
      );
    }
  }

  @Action(GetBotCollections)
  getBotCollections(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotCollections
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotCollections(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              collections: result.collections,
            });
          }
        })
      );
    }
  }

  @Action(GetBotCategories)
  getBotCategories(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotCategories
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotCategories(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              categories: result.categories,
            });
          }
        })
      );
    }
  }

  @Action(GetBotProducts)
  getBotProducts(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotProducts
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotProducts(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              products: result.products,
            });
          }
        })
      );
    }
  }

  @Action(GetBotTags)
  getBotTags(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotTags
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotTags(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              tags: result.tags,
            });
          }
        })
      );
    }
  }

  @Action(UpdateBotTag)
  updateBotTag(
    { getState, setState }: StateContext<BotStateModel>,
    { tag }: UpdateBotTag
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.updateBotTag(state.botId, tag).pipe(
        tap((result) => {
          if (result.status) {
            setState(
              patch({
                tags: updateItem<any>(
                  (updTag) => updTag?.tag_id === tag.tag_id,
                  tag
                ),
              })
            );
          }
        })
      );
    }
  }

  @Action(GetBotTeamExtended)
  getBotTeamExtended({
    getState,
    patchState,
  }: StateContext<BotStateModel>): any {
    return this.botService.getBotTeam(getState().botId).pipe(
      tap((result) => {
        if (result.status) {
          patchState({ teamExtended: [result.creator, ...result.team] });
        }
      })
    );
  }

  @Action(GetBotMails)
  getBotMails(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotMails
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotMails(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              mails: result.mails.filter((mail: any) => mail.type === 'custom'),
            });
          }
        })
      );
    }
  }

  @Action(GetBotEntities)
  getBotEntities(
    { getState, setState }: StateContext<BotStateModel>,
    {}: GetBotEntities
  ): any {
    const state = getState();
    if (state.botId) {
      return this.botService.getBotEntities(state.botId).pipe(
        tap((result) => {
          if (result.status) {
            const state = getState();
            setState({
              ...state,
              entities: result.entities,
            });
          }
        })
      );
    }
  }

  // Bot dashboard

  @Action(GetBotTeam)
  getBotTeam({ getState, patchState }: StateContext<BotStateModel>): any {
    return this.botService.getBotTeam(getState().botId).pipe(
      tap((result) => {
        if (result.status) {
          patchState({ team: result.team });
        }
      })
    );
  }
}
